import { useState, useEffect, useRef } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Brain, Database, Search, Loader2, Trash2, FileText, Sparkles, RefreshCw, Upload } from 'lucide-react';
import { toast } from 'sonner';

export default function KnowledgeBasePage() {
  const [docs, setDocs] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('castigata');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const loadDocs = async () => {
    try { const r = await API.get('/kb/documents'); setDocs(r.data); return r.data; }
    catch { return []; }
  };

  useEffect(() => {
    loadDocs();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    const processing = docs.some(d => d.status === 'processing');
    if (processing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await loadDocs();
        if (!d.some(x => x.status === 'processing')) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 5000);
    }
  }, [docs]);

  const seed = async () => {
    setSeeding(true);
    try { const r = await API.post('/kb/seed-won-tenders'); toast.success(r.data.message); loadDocs(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setSeeding(false); }
  };

  const del = async (id) => {
    try { await API.delete(`/kb/document/${id}`); toast.success('Eliminat'); loadDocs(); } catch { toast.error('Eroare'); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      fd.append('category', uploadCategory);
      const r = await API.post('/kb/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data.message);
      loadDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare incarcare'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try { const r = await API.post('/kb/search', { query, top_k: 6 }); setResults(r.data.results); if (!r.data.results.length) toast.info('Niciun rezultat'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare cautare'); }
    finally { setSearching(false); }
  };

  const totalChunks = docs.reduce((s, d) => s + (d.chunks_count || 0), 0);

  return (
    <div className="p-8" data-testid="knowledge-base-page">
      <div className="flex items-center gap-3 mb-2">
        <Brain className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Baza de Cunostinte ML</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">AI invata din licitatiile castigate anterior. Cautare semantica (embeddings OpenAI) peste documentele indexate.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: documents */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Licitatii Castigate</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Introdu SL 01 (final) si SL 02 (GLINA IF) in modelul ML pentru referinta la scoring si generare oferte.</p>
              <Button onClick={seed} disabled={seeding} className="w-full" data-testid="kb-seed-btn">
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                {seeding ? 'Se porneste...' : 'Indexeaza SL 01 + SL 02'}
              </Button>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv" onChange={uploadFile} className="hidden" data-testid="kb-upload-input" />
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                <button type="button" onClick={() => setUploadCategory('castigata')} data-testid="kb-cat-castigata"
                  className={`text-[10px] py-1.5 rounded-md border transition-colors ${uploadCategory === 'castigata' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400' : 'border-white/10 text-muted-foreground hover:border-white/20'}`}>Licitatie castigata</button>
                <button type="button" onClick={() => setUploadCategory('lessons')} data-testid="kb-cat-lessons"
                  className={`text-[10px] py-1.5 rounded-md border transition-colors ${uploadCategory === 'lessons' ? 'bg-rose-500/15 border-rose-500/40 text-rose-400' : 'border-white/10 text-muted-foreground hover:border-white/20'}`}>Motive pierdere / competitori</button>
              </div>
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="w-full mt-2 border-primary/30 text-primary hover:bg-primary/10" data-testid="kb-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Se incarca...' : (uploadCategory === 'lessons' ? 'Incarca raport / motive pierdere' : 'Incarca alta licitatie castigata')}
              </Button>
              <p className="text-[10px] text-muted-foreground mt-1.5">{uploadCategory === 'lessons' ? 'Rapoarte de procedura / analize competitori. AI-ul le foloseste ca sa evite motivele de pierdere in ofertele generate.' : 'Accepta PDF, Word, Excel, TXT. AI-ul invata automat din fiecare licitatie adaugata.'}</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-white/10">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Documente Indexate ({docs.length})</CardTitle>
              <button onClick={loadDocs} className="text-muted-foreground hover:text-foreground" data-testid="kb-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
            </CardHeader>
            <CardContent className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="p-2.5 rounded-lg bg-zinc-900/50 border border-white/5" data-testid={`kb-doc-${d.source}`}>
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                    <p className="text-xs font-medium truncate flex-1">{d.title}</p>
                    <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 ml-5">
                    {d.status === 'processing' ? <Badge className="bg-amber-500/10 text-amber-400 text-[9px]"><Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />Se proceseaza (OCR)</Badge>
                      : d.status === 'error' ? <Badge className="bg-rose-500/10 text-rose-400 text-[9px]">Eroare</Badge>
                      : <Badge className="bg-emerald-500/10 text-emerald-400 text-[9px]">{d.chunks_count} fragmente</Badge>}
                    {(d.tags || []).map(t => <span key={t} className="text-[9px] text-muted-foreground">#{t}</span>)}
                  </div>
                </div>
              ))}
              {!docs.length && <p className="text-xs text-muted-foreground text-center py-4">Baza de cunostinte este goala</p>}
              {docs.length > 0 && <p className="text-[10px] text-muted-foreground pt-1">Total: {totalChunks} fragmente vectorizate</p>}
            </CardContent>
          </Card>
        </div>

        {/* Right: semantic search */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-card border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Search className="w-4 h-4 text-primary" />Cautare Semantica</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
                  placeholder="Ex: metodologie asfaltare, deviz manopera, garantie de buna executie..." className="bg-zinc-900/50 border-white/10" data-testid="kb-search-input" />
                <Button onClick={search} disabled={searching || !query.trim()} data-testid="kb-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <div className="space-y-3">
                {results.map((r, i) => (
                  <div key={i} className="p-3 rounded-lg bg-zinc-900/40 border border-white/5" data-testid={`kb-result-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-medium text-primary truncate">{r.title}</span>
                      <Badge className="bg-emerald-500/10 text-emerald-400 text-[9px] font-mono-data shrink-0">{(r.score * 100).toFixed(0)}% relevanta</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{r.text.slice(0, 500)}{r.text.length > 500 ? '…' : ''}</p>
                  </div>
                ))}
                {!results.length && <p className="text-xs text-muted-foreground text-center py-8">Cauta in licitatiile castigate si documentele indexate</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
